<template>
  <button :disabled="disabled" class="action-button" @click="click">
    <svg>
      <circle
        v-if="isReady"
        stroke="currentColor"
        :stroke-dasharray="circumference + ' ' + circumference"
        :style="{ strokeDashoffset: strokeDashoffset }"
        :stroke-width="stroke"
        fill="transparent"
        :r="radius"
        cx="50%"
        cy="50%"
      />
    </svg>
    <span class="action-button-content">
      <slot />
    </span>
  </button>
</template>
<script>
let instance = null;

export default {
  props: {
    keyCode: {
      type: Number,
      default: null
    },
    timeout: {
      type: Number,
      default: 1000
    },
    stroke: {
      type: Number,
      default: 5
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isReady: false,
      keypressed: false,
      radius: null,
      circumference: null,
      progress: 0
    };
  },
  computed: {
    strokeDashoffset() {
      return this.circumference - (Math.min(this.progress, 100) / 100) * this.circumference;
    }
  },
  mounted() {
    this.initialize();
  },
  created() {
    window.addEventListener('resize', this.initialize);
    if (this.keyCode) {
      window.addEventListener('keyup', this.keyup);
      window.addEventListener('keydown', this.keydown);
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.initialize);
    if (this.keyCode) {
      window.removeEventListener('keyup', this.keyup);
      window.removeEventListener('keydown', this.keydown);
    }
  },
  methods: {
    click() {
      this.$emit('click');
    },
    initialize() {
      this.radius = this.$el.getBoundingClientRect().width / 2 - this.stroke / 2;
      this.circumference = this.radius * 2 * Math.PI;
      this.isReady = true;
    },
    keydown(event) {
      if (instance || this.keyCode !== event.keyCode || this.keypressed || this.progress === 110) {
        return;
      }

      instance = this._uid;

      this.keypressed = true;
      this.poll();
    },
    keyup(event) {
      if (this.keyCode !== event.keyCode) {
        return;
      }

      instance = null;

      this.keypressed = false;
    },
    async poll() {
      if (this.progress === 110) {
        this.click();
        return;
      }

      if (!this.keypressed) {
        this.progress = 0;
        return;
      }

      const step = 20;

      this.progress = Math.min(this.progress + 100 / step, 110);
      await new Promise(resolve => setTimeout(resolve, this.timeout / step));
      await this.poll();
    }
  }
};
</script>

<style lang="scss" scoped>
.action-button {
  font-size: 6rem;
  background: transparent;
  border: 0 transparent none;
  cursor: pointer;
  padding: 0;
  font-weight: bold;
  color: inherit;
  border-radius: 50%;
  width: 100%;
  height: 175px;
  box-sizing: border-box;
  text-transform: uppercase;
  line-height: 1;
  font-family: monospace;
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
  }

  .action-button-content {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -60%);
  }
}

circle {
  transition: stroke-dashoffset 0.15s;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}
</style>
